import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router';
import { showToast } from '../../../ui/utils';
import { PageWrapper } from '../../../ui/PageWrapper';
import { ConfirmModal } from '../../../ui/ConfirmModal/ConfirmModal';
import { OfferForm } from '../components/OfferForm/OfferForm';
import { OfferFormValues } from '../components/OfferForm/offerFormLogic';
import { useCreateOffer } from '../useCreateOffer';
import { useUnsavedChangesGuard } from '../useUnsavedChangesGuard';
import { useDuplicateOfferFormValues } from './useDuplicateOfferFormValues';

function getPageLayout() {
  return {
    header: { title: 'New offer', settings: 'admin' },
    sidebar: {
      nav: [
        {
          title: 'Back',
          href: '/home/',
          action: window.history.length > 1 && document.referrer !== window.location.href
            ? () => { window.history.back(); } : null,
          variant: 'secondary',
        },
        { title: 'Offers', href: '/offers' },
      ],
    },
  };
}

export function NewOfferItemPage(props: RouteComponentProps) {
  const { history, location } = props;
  const pageLayout = getPageLayout();
  const { isLoading, createOffer } = useCreateOffer();
  const [submitError, setSubmitError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const {
    isConfirmVisible, confirmLeave, cancelLeave, allowNextNavigation,
  } = useUnsavedChangesGuard(hasUnsavedChanges);
  const offerFormValues = useDuplicateOfferFormValues(location);

  const handleSubmit = (values: OfferFormValues) => {
    setSubmitError('');
    createOffer(values)
      .then((offer) => {
        // A toast is portal-rendered into a DOM node App mounts outside the router-swapped
        // content, so it survives the redirect below rather than needing a location-state flag
        // carried to the edit page the way the old top-of-page banner did.
        showToast({ textMessage: `Offer "${offer.display_name}" saved.`, variant: 'success' });
        // The just-created offer's own values are, by definition, no longer "unsaved" -- this
        // redirect is the one navigation the guard must let through without asking.
        allowNextNavigation();
        history.replace(`/offers/${offer.id}`);
      })
      .catch((error) => {
        setSubmitError(error.message);
        showToast({ textMessage: error.message, variant: 'danger', timeout: 7000 });
      });
  };

  return (
    <PageWrapper {...props} page={pageLayout}>
      <Card>
        <Card.Body>
          <Card.Title>New offer</Card.Title>
          <OfferForm
            offerFormValues={offerFormValues}
            onSubmit={handleSubmit}
            onCancel={() => history.push('/offers')}
            isLoading={isLoading}
            isEditing={false}
            submitError={submitError}
            onDirtyChange={setHasUnsavedChanges}
          />
        </Card.Body>
      </Card>
      <ConfirmModal
        centered
        show={isConfirmVisible}
        isLoading={false}
        title="Unsaved changes"
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
        confirmButtonTitle="Leave"
        cancelButtonTitle="Stay"
        backdrop="static"
      >
        You have unsaved changes. Leave without saving?
      </ConfirmModal>
    </PageWrapper>
  );
}
